import React from 'react';
import './UnitIcon.css'
import {getPlayerHexColor} from "../../../../utils/players/getPlayerHexColor";
import {getFactionUnitSkinIconPath} from "../../../../utils/factions/getFactionUnitSkinIconPath";
import {NEUTRAL_PLAYER_COLOR} from "../../../../constants/players";
import {getFactionConfig} from "../../../../utils/factions/getFactionConfig";
import {getUnitTypeConfig} from "../../../../configs/units";
import {FACTIONS} from "../../../../constants/factions";

// Нейтральный отряд черный, как на поле, и на темных панелях его отделяет белая обводка. Она на
// обертке: маска иконки срезала бы собственный filter элемента
const withNeutralOutline = (icon, isNeutral) => (
  isNeutral ? <div className={'unit-icon_neutral-outline'}>{icon}</div> : icon
);

const UnitIcon = ({
  unitType, playerColor, className, factionId, hasUnitSkin, useNeutralIcon, capturedOriginalColor,
}) => {
  // useNeutralIcon позволяет отобразить нейтральную иконку-силуэт независимо
  // от цвета тона (playerColor): у соблазнённого Суккубом нейтрального
  // отряда playerColor — цвет игрока-суккуба (тон левой половины), а форма
  // силуэта при этом должна оставаться нейтральной (см. getUnitIconDisplayProps
  // и usesNeutralIcon в HexGridRenderer.renderUnit). Если проп не передан,
  // определяем как раньше — по playerColor
  const isNeutralIcon = useNeutralIcon ?? (playerColor === NEUTRAL_PLAYER_COLOR);

  const style = {
    backgroundColor: getPlayerHexColor(playerColor),
  };

  // Нейтральные отряды не привязаны к фракции — вместо названия фракции
  // всегда «Нейтрал» (как ник нейтрального владельца в InspectedCellPanel)
  const factionName = isNeutralIcon
    ? 'Нейтрал'
    : factionId && getFactionConfig(factionId)?.name;
  const unitDescription = getUnitTypeConfig(unitType)?.description;
  const title = factionName && unitDescription ? `${factionName}: ${unitDescription}` : undefined;

  // Нейтральные отряды всегда получают собственный набор иконок (по одной на
  // тип отряда, /img/factions/unit_icons/unit_dX_neutral.png) вместо общего
  // силуэта unit_dX_white — во всех режимах и видах партий, как на игровом
  // поле (см. renderUnit в HexGridRenderer)
  if (isNeutralIcon) {
    const neutralIconUrl = `url(${getFactionUnitSkinIconPath(unitType, NEUTRAL_PLAYER_COLOR)})`;
    style.maskImage = neutralIconUrl;
    style.WebkitMaskImage = neutralIconUrl;
  } else if (hasUnitSkin && factionId) {
    // Как на игровом поле (HexGridRenderer.renderUnit): если у игрока-владельца
    // открыт фракционный скин отрядов, силуэт-маска подменяется на его иконку
    const skinIconUrl = `url(${getFactionUnitSkinIconPath(unitType, factionId)})`;
    style.maskImage = skinIconUrl;
    style.WebkitMaskImage = skinIconUrl;
  } else if (factionId === FACTIONS.GIANTS) {
    // Великаны без купленного/применённого скина - собственная дефолтная
    // иконка вместо общего силуэта (см. renderUnit в HexGridRenderer)
    const defaultIconUrl = `url(/img/factions/unit_icons/${unitType}_giants_default.png)`;
    style.maskImage = defaultIconUrl;
    style.WebkitMaskImage = defaultIconUrl;
  }

  // Отряд, соблазнённый Суккубом (или другой захват со сменой владельца):
  // как на игровом поле, правая половина иконки остаётся тона исходного
  // владельца, левая — тона текущего (см. getCapturedUnitOriginalColor)
  if (capturedOriginalColor != null) {
    const overlayStyle = {
      ...style,
      backgroundColor: getPlayerHexColor(capturedOriginalColor),
    };
    // className (масштаб/смещение конкретного места использования, см.
    // getUnitIconClassNameForButton) переносится на ОБА внутренних слоя, а не
    // на unit-icon_two-tone - тот служит только контейнером для позиционирования
    // (position: relative), чтобы не задваивать/не терять transform из className
    // при пересечении с более специфичными CSS-правилами вида
    // ".selector .unit-icon" (см. SelectedUnitStats.css)
    return withNeutralOutline(
      <div className={'unit-icon_two-tone'} title={title}>
        <div className={`unit-icon ${unitType} ${className}`} style={style}/>
        <div className={`unit-icon unit-icon_captured-half ${unitType} ${className}`} style={overlayStyle}/>
      </div>,
      playerColor === NEUTRAL_PLAYER_COLOR || capturedOriginalColor === NEUTRAL_PLAYER_COLOR
    );
  }

  return withNeutralOutline(
    <div
      className={`unit-icon ${unitType} ${className}`}
      style={style}
      title={title}
    />,
    playerColor === NEUTRAL_PLAYER_COLOR
  );
};

export default UnitIcon;